'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { projectService } from '@/services/projectService';

interface PublicNavigationProps {
  className?: string;
}

const PublicNavigation = ({ className = '' }: PublicNavigationProps) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const firstMobileLinkId = 'mobile-first-link';
  const firstDesktopLinkRef = useRef<HTMLAnchorElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const hasFocusedNav = useRef(false);
  const pathname = usePathname();
  const firstMobileLinkRef = useRef<HTMLAnchorElement | null>(null);

  const navigationItems: Array<{ label: string; path: string; icon: string; hide?: boolean }> = [
    // Home link removed - logo serves as home link with aria-label
    { label: 'Writing', path: '/thoughts-feed', icon: 'DocumentTextIcon' },
    { label: 'Projects', path: '/projects-index', icon: 'FolderIcon' },
  ];

  // Default to hiding project link until we know projects exist
  const [hideProjects, setHideProjects] = useState(true);

  useEffect(() => {
    let mounted = true;

    const hydrateFromSession = () => {
      const storedProjects = sessionStorage.getItem('hasProjects');
      if (storedProjects !== null) setHideProjects(storedProjects !== 'true');
      return storedProjects !== null;
    };

    const checkContentAvailability = async () => {
      // If session already has both flags, trust it and skip network
      const hasSession = hydrateFromSession();
      if (hasSession) return;

      try {
        const projectsResult = await projectService.getAllPublished();

        const hasProjectsData = Boolean(projectsResult.data && projectsResult.data.length > 0);

        if (!mounted) return;

        setHideProjects(!hasProjectsData);

        sessionStorage.setItem('hasProjects', hasProjectsData ? 'true' : 'false');
      } catch {
        // On error, keep default to avoid showing unavailable sections
        if (!mounted) return;
        setHideProjects(true);
      }
    };

    checkContentAvailability();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Ensure we start near the top so menu button stays in viewport
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0 });
    }
  }, []);

  // If nothing is focused on load, move focus to first nav link (helps keyboard/a11y)
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const active = document.activeElement;
    if (active === document.body || active === document.documentElement || !active) {
      setTimeout(() => firstDesktopLinkRef.current?.focus(), 0);
    }
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMobileOpen(false);
        return;
      }
      if (event.key === 'Tab') {
        const firstLink = firstMobileLinkRef.current || (document.getElementById(firstMobileLinkId) as HTMLAnchorElement | null);
        if (firstLink) {
          event.preventDefault();
          firstLink.focus();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [mobileOpen]);

  // When tabbing from skip link or initial state, move focus to first nav link
  useEffect(() => {
    const handleTabFromSkip = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const active = document.activeElement as HTMLAnchorElement | null;
      const isSkip = active?.getAttribute?.('href') === '#main-content';
      const isNone = !active || active === document.body;
      const isMenuButton = active === menuButtonRef.current;

      // First Tab after skip or when no focus/menu button: focus first nav link; Shift+Tab allowed to go back
      if (!event.shiftKey && (isSkip || isNone || isMenuButton)) {
        event.preventDefault();
        firstDesktopLinkRef.current?.focus();
        hasFocusedNav.current = true;
      }
    };
    window.addEventListener('keydown', handleTabFromSkip);
    return () => window.removeEventListener('keydown', handleTabFromSkip);
  }, []);

  const isActivePath = (path: string) => {
    if (pathname === path) return true;
    // Also match if pathname starts with the path (for nested routes)
    if (pathname && pathname.startsWith(path) && path !== '/') return true;
    return false;
  };

  return (
    <>
      <header data-component-id="src/components/common/PublicNavigation.tsx:143:6" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="143" data-component-end-line="208" data-component-file="PublicNavigation.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
        className={`fixed top-0 left-0 right-0 z-[100] bg-white transition-smooth ${
          scrolled ? 'shadow-editorial-md' : 'shadow-editorial-sm'
        } ${className}`}
        role="banner"
      >
        <div data-component-id="src/components/common/PublicNavigation.tsx:149:8" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="149" data-component-end-line="207" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20px-6%20lg%3Apx-8%22%7D" className="max-w-7xl mx-auto px-6 lg:px-8">
          <div data-component-id="src/components/common/PublicNavigation.tsx:150:10" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="150" data-component-end-line="206" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20h-20%22%7D" className="flex items-center justify-between h-20">
            <div data-component-id="src/components/common/PublicNavigation.tsx:151:12" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="151" data-component-end-line="161" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%22%7D" className="flex items-center">
              <Link data-component-id="src/components/common/PublicNavigation.tsx:152:14" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="152" data-component-end-line="160" data-component-file="PublicNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fhome-landing%22%2C%22className%22%3A%22font-sans%20font-bold%20text-%5B2.625rem%5D%20tracking-tight%20focus%3Aoutline-none%20focus%3Aring-4%20focus%3Aring-%5Bvar(--focus)%5D%20rounded-%5Bvar(--radius-2)%5D%22%7D" 
                href="/home-landing"
                className="font-sans font-bold text-[2.625rem] tracking-tight focus:outline-none focus:ring-4 focus:ring-[var(--focus)] rounded-[var(--radius-2)]"
                aria-label="Salt.Work - Home"
                ref={firstDesktopLinkRef}
              >
                <span data-component-id="src/components/common/PublicNavigation.tsx:158:16" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="158" data-component-end-line="158" data-component-file="PublicNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-black%22%2C%22textContent%22%3A%22SALT%22%7D" className="text-black">SALT</span>
                <span data-component-id="src/components/common/PublicNavigation.tsx:159:16" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="159" data-component-end-line="159" data-component-file="PublicNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-gray-500%22%2C%22textContent%22%3A%22.WORK%22%7D" className="text-gray-500">.WORK</span>
              </Link>
            </div>

            <nav data-component-id="src/components/common/PublicNavigation.tsx:163:12" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="163" data-component-end-line="205" data-component-file="PublicNavigation.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22flex%20items-center%20w-full%20justify-end%22%7D" 
              className="flex items-center w-full justify-end"
              aria-label="Main navigation"
            >
              <div data-component-id="src/components/common/PublicNavigation.tsx:167:14" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="167" data-component-end-line="189" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20space-x-1%20ml-auto%22%7D" className="hidden lg:flex items-center space-x-1 ml-auto">
                {navigationItems
                  .filter((item) => !(item.label === 'Projects' && hideProjects))
                  .map((item) => {
                  const isActive = isActivePath(item.path);
                  return (
                    <Link data-component-id="src/components/common/PublicNavigation.tsx:173:20" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="173" data-component-end-line="186" data-component-file="PublicNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                      key={item.path}
                      href={item.path}
                      aria-current={!mobileOpen && isActive ? 'page' : undefined}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-smooth ${
                        isActive
                          ? 'text-foreground font-semibold underline underline-offset-4 decoration-2'
                          : 'text-muted-foreground hover:text-foreground hover:-translate-y-px focus:outline-none focus:ring-4 focus:ring-[var(--focus)]'
                      }`}
                      /* Logo is first focusable element, no ref needed here */
                    >
                             <Icon data-component-id="src/components/common/PublicNavigation.tsx:184:29" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="184" data-component-end-line="184" data-component-file="PublicNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={item.icon} size={20} aria-hidden="true" />
                      <span data-component-id="src/components/common/PublicNavigation.tsx:185:22" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="185" data-component-end-line="185" data-component-file="PublicNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%22%7D" className="font-medium">{item.label}</span>
                    </Link>
                  );
                })}
              </div>

              <div data-component-id="src/components/common/PublicNavigation.tsx:191:14" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="191" data-component-end-line="204" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20lg%3Ahidden%20fixed%20top-3%20right-3%20z-%5B110%5D%22%7D" className="flex lg:hidden fixed top-3 right-3 z-[110]">
                <button data-component-id="src/components/common/PublicNavigation.tsx:192:16" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="192" data-component-end-line="203" data-component-file="PublicNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22inline-flex%20items-center%20justify-center%20rounded-md%20p-2%20text-foreground%20focus%3Aoutline-none%20focus%3Aring-4%20focus%3Aring-%5Bvar(--focus)%5D%22%7D"
                  type="button"
                  aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
                  onClick={() => {
                    const next = !mobileOpen;
                    setMobileOpen(next);
                  }}
                  ref={menuButtonRef}
                  className="inline-flex items-center justify-center rounded-md p-2 text-foreground focus:outline-none focus:ring-4 focus:ring-[var(--focus)]"
                >
                  <Icon data-component-id="src/components/common/PublicNavigation.tsx:202:18" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="202" data-component-end-line="202" data-component-file="PublicNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={mobileOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} aria-hidden="true" />
                </button>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {mobileOpen && (
        <div data-component-id="src/components/common/PublicNavigation.tsx:211:8" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="211" data-component-end-line="255" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22mobile-menu%22%2C%22className%22%3A%22fixed%20inset-0%20z-%5B90%5D%20bg-black%2F40%20backdrop-blur-sm%20lg%3Ahidden%22%7D"
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[90] bg-black/40 backdrop-blur-sm lg:hidden"
        >
          <button data-component-id="src/components/common/PublicNavigation.tsx:217:10" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="217" data-component-end-line="222" data-component-file="PublicNavigation.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22absolute%20inset-0%22%7D"
            type="button"
            className="absolute inset-0"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <div data-component-id="src/components/common/PublicNavigation.tsx:223:10" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="223" data-component-end-line="254" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-20%20left-0%20right-0%20mx-auto%20max-w-7xl%20px-6%20lg%3Apx-8%22%7D"
            className="absolute top-20 left-0 right-0 mx-auto max-w-7xl px-6 lg:px-8"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            <div data-component-id="src/components/common/PublicNavigation.tsx:228:12" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="228" data-component-end-line="253" data-component-file="PublicNavigation.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22rounded-xl%20bg-white%20shadow-editorial-md%20border%20border-border%20p-4%20space-y-2%22%7D" className="rounded-xl bg-white shadow-editorial-md border border-border p-4 space-y-2">
              {navigationItems
                .filter((item) => !(item.label === 'Projects' && hideProjects))
                .map((item, idx) => {
                const isActive = isActivePath(item.path);
                return (
                  <Link data-component-id="src/components/common/PublicNavigation.tsx:234:18" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="234" data-component-end-line="250" data-component-file="PublicNavigation.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                    key={item.path}
                    href={item.path}
                    aria-current={isActive ? 'page' : undefined}
                    className={`flex items-center gap-3 px-3 py-2 rounded-lg focus:outline-none focus:ring-4 focus:ring-[var(--focus)] ${
                      isActive
                        ? 'bg-primary/10 text-foreground font-semibold'
                        : 'text-muted-foreground hover:bg-muted/60'
                    }`}
                    onClick={() => setMobileOpen(false)}
                    id={idx === 0 ? firstMobileLinkId : undefined}
                    tabIndex={idx === 0 ? 0 : undefined}
                    ref={idx === 0 ? firstMobileLinkRef : undefined}
                  >
                           <Icon data-component-id="src/components/common/PublicNavigation.tsx:248:27" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="248" data-component-end-line="248" data-component-file="PublicNavigation.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={item.icon} size={20} aria-hidden="true" />
                    <span data-component-id="src/components/common/PublicNavigation.tsx:249:20" data-component-path="src/components/common/PublicNavigation.tsx" data-component-line="249" data-component-end-line="249" data-component-file="PublicNavigation.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PublicNavigation;